import { useCallback, useState } from "react";

import { useNavigate } from "react-router-dom";

import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Download,
  Search,
} from "lucide-react";
import * as XLSX from "xlsx";

import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Skeleton } from "~/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";

import type {
  DailyClientDebtDetail,
  SortColumn,
  SortDirection,
} from "../types";

interface ClientDebtTableProps {
  clients: DailyClientDebtDetail[];
  totalDebt: number;
  totalClients: number;
  totalPages: number;
  currentPage: number;
  loading?: boolean;
  searchTerm: string;
  sortColumn: SortColumn;
  sortDirection: SortDirection;
  onSearchChange: (term: string) => void;
  onSortChange: (column: SortColumn) => void;
  onPageChange: (page: number) => void;
}

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat("es-CL", {
    style: "currency",
    currency: "CLP",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

const formatPercentage = (value: number): string => {
  return new Intl.NumberFormat("es-CL", {
    style: "percent",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value / 100);
};

export const ClientDebtTable = ({
  clients,
  totalDebt,
  totalClients,
  totalPages,
  currentPage,
  loading = false,
  searchTerm,
  sortColumn,
  sortDirection,
  onSearchChange,
  onSortChange,
  onPageChange,
}: ClientDebtTableProps) => {
  const navigate = useNavigate();
  const [localSearch, setLocalSearch] = useState(searchTerm);

  const handleClientClick = useCallback(
    (clientId: string) => {
      navigate(`/daily-debt-snapshots/client/${clientId}`);
    },
    [navigate]
  );

  const handleSearchSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      onSearchChange(localSearch);
    },
    [localSearch, onSearchChange]
  );

  const handleExportExcel = useCallback(() => {
    if (clients.length === 0) return;

    const rows = clients.map((client) => ({
      Cliente: client.client_name,
      RUT: client.client_rut,
      Deuda: client.debt_amount,
      "% del Total": totalDebt > 0 ? (client.debt_amount / totalDebt) * 100 : 0,
    }));

    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Deuda Clientes");

    XLSX.writeFile(
      workbook,
      `deuda-clientes-${new Date().toISOString().split("T")[0]}.xlsx`
    );
  }, [clients, totalDebt]);

  const getSortIcon = (column: SortColumn) => {
    if (sortColumn !== column) {
      return <ArrowUpDown className="ml-2 h-4 w-4" />;
    }
    return sortDirection === "asc" ? (
      <ArrowUp className="ml-2 h-4 w-4" />
    ) : (
      <ArrowDown className="ml-2 h-4 w-4" />
    );
  };

  const renderPagination = () => {
    const pages: (number | string)[] = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);

      if (currentPage > 3) {
        pages.push("...");
      }

      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);

      for (let i = start; i <= end; i++) {
        if (!pages.includes(i)) {
          pages.push(i);
        }
      }

      if (currentPage < totalPages - 2) {
        pages.push("...");
      }

      if (!pages.includes(totalPages)) {
        pages.push(totalPages);
      }
    }

    return (
      <div className="mt-4 flex items-center justify-between">
        <div className="text-sm text-muted-foreground">
          Mostrando {clients.length} de {totalClients.toLocaleString("es-CL")}{" "}
          clientes
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="icon"
            onClick={() => onPageChange(1)}
            disabled={currentPage === 1 || loading}
          >
            <ChevronsLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1 || loading}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>

          {pages.map((page, index) =>
            page === "..." ? (
              <span
                key={`ellipsis-${index}`}
                className="px-2 text-muted-foreground"
              >
                ...
              </span>
            ) : (
              <Button
                key={page}
                variant={currentPage === page ? "default" : "outline"}
                size="icon"
                onClick={() => onPageChange(page as number)}
                disabled={loading}
              >
                {page}
              </Button>
            )
          )}

          <Button
            variant="outline"
            size="icon"
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages || loading}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            onClick={() => onPageChange(totalPages)}
            disabled={currentPage === totalPages || loading}
          >
            <ChevronsRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between gap-4">
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-10 w-32" />
        </div>
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>
                  <Skeleton className="h-4 w-24" />
                </TableHead>
                <TableHead>
                  <Skeleton className="h-4 w-20" />
                </TableHead>
                <TableHead>
                  <Skeleton className="h-4 w-20" />
                </TableHead>
                <TableHead>
                  <Skeleton className="h-4 w-16" />
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {Array.from({ length: 10 }).map((_, i) => (
                <TableRow key={i}>
                  <TableCell>
                    <Skeleton className="h-4 w-40" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-24" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-24" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-12" />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
        <form
          onSubmit={handleSearchSubmit}
          className="flex w-full items-center gap-2 sm:w-auto"
        >
          <div className="relative flex-1 sm:w-64">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Buscar por nombre o RUT..."
              value={localSearch}
              onChange={(e) => setLocalSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <Button type="submit" variant="secondary" size="sm">
            Buscar
          </Button>
        </form>

        <Button
          variant="outline"
          size="sm"
          onClick={handleExportExcel}
          disabled={clients.length === 0}
        >
          <Download className="mr-2 h-4 w-4" />
          Exportar Excel
        </Button>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>
                <Button
                  variant="ghost"
                  onClick={() => onSortChange("client_name")}
                  className="-ml-4"
                >
                  Cliente
                  {getSortIcon("client_name")}
                </Button>
              </TableHead>
              <TableHead className="hidden sm:table-cell">
                <Button
                  variant="ghost"
                  onClick={() => onSortChange("client_rut")}
                  className="-ml-4"
                >
                  RUT
                  {getSortIcon("client_rut")}
                </Button>
              </TableHead>
              <TableHead className="text-right">
                <Button
                  variant="ghost"
                  onClick={() => onSortChange("debt_amount")}
                  className="-mr-4"
                >
                  Deuda
                  {getSortIcon("debt_amount")}
                </Button>
              </TableHead>
              <TableHead className="hidden text-right md:table-cell">
                % del Total
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {clients.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={4}
                  className="py-8 text-center text-muted-foreground"
                >
                  No se encontraron clientes
                </TableCell>
              </TableRow>
            ) : (
              clients.map((client, index) => {
                const percentage =
                  totalDebt > 0 ? (client.debt_amount / totalDebt) * 100 : 0;

                return (
                  <TableRow
                    key={client.id}
                    className="cursor-pointer duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both hover:bg-muted/50"
                    style={{ animationDelay: `${Math.min(index * 40, 400)}ms` }}
                    onClick={() => handleClientClick(client.client_id)}
                  >
                    <TableCell className="font-medium">
                      {client.client_name}
                      <span className="mt-1 block text-xs text-muted-foreground sm:hidden">
                        {client.client_rut}
                      </span>
                    </TableCell>
                    <TableCell className="hidden sm:table-cell">
                      {client.client_rut}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {formatCurrency(client.debt_amount)}
                    </TableCell>
                    <TableCell className="hidden text-right text-muted-foreground md:table-cell">
                      {formatPercentage(percentage)}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {totalPages > 1 && renderPagination()}
    </div>
  );
};
